/**
 * AASB 138 — Intangible Assets. Rulepack v1.0.0.
 * Mirrors AASB 116 mechanics (straight-line amortisation reconciliation).
 * Indefinite-life intangibles are flagged but not amortised — they require AASB 136 annual impairment test.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseDate, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt, monthsBetween,
} from './_shared';

const VERSION = 'aasb138-1.0.0';
const fb = findingBuilder('AASB_138' as any, VERSION);

const HMAP = makeHeaderMap([
  ['asset_id', 'asset id', 'intangible id', 'id'],
  ['asset_class', 'asset class', 'class', 'category'],
  ['acquisition_date', 'acquisition date', 'purchase date', 'recognised date'],
  ['cost', 'cost', 'historical cost'],
  ['useful_life_years', 'useful life', 'useful life (years)', 'life'],
  ['amortisation_method', 'amortisation method', 'method', 'amortization method'],
  ['accumulated_amortisation', 'accumulated amortisation', 'accum amortisation', 'accumulated amortization'],
  ['carrying_value', 'carrying value', 'net book value', 'nbv'],
  ['indefinite_life', 'indefinite life', 'indefinite', 'indefinite-life'],
  ['as_at_date', 'as at', 'as at date', 'reporting date'],
]);

const REQUIRED = ['asset_id', 'cost', 'accumulated_amortisation', 'carrying_value'];

export function detectAASB138(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  // Boost confidence if "intangible" / "amortisation" keywords are in headers
  const boostKeywords = ['intangible', 'amortisation', 'amortization'];
  const hasKeyword = headers.some(h => boostKeywords.some(k => String(h ?? '').toLowerCase().includes(k)));
  return {
    confidence: Math.min(1, (matched.length / REQUIRED.length) * (hasKeyword ? 1.2 : 1)),
    matched, missing: REQUIRED.filter(r => !hits.has(r)),
  };
}

export function validateAASB138(headers: string[], data: CellValue[][], asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_138' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('asset_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB138.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const cost = parseNumber(get('cost'));
    const life = idx.useful_life_years !== undefined ? parseNumber(get('useful_life_years')) : null;
    const acc = parseNumber(get('accumulated_amortisation'));
    const cv = parseNumber(get('carrying_value'));
    const acqD = idx.acquisition_date !== undefined ? parseDate(get('acquisition_date')) : null;
    const indefinite = idx.indefinite_life !== undefined ? Boolean(get('indefinite_life')) : false;

    if (!(cost !== null && cost > 0)) { findings.push(fb.build('AASB138.R02', 'error', 'Cost must be positive', `Asset ${id}: cost ${cost}.`, { rowId: id })); return; }

    // R03 indefinite-life flagged
    if (indefinite || life === null || (life !== null && life === 0)) {
      if (acc! > 0.01) {
        findings.push(fb.build('AASB138.R03', 'warning', 'Indefinite-life intangible should not be amortised',
          `Asset ${id}: indefinite life or no useful life specified, but accumulated amortisation $${fmt(acc!)}. AASB 138.107: indefinite-life intangibles are NOT amortised; instead tested for impairment annually under AASB 136.`,
          { rowId: id, evidence: { acc, life, indefinite } }));
      } else {
        findings.push(fb.build('AASB138.R03', 'info', 'Indefinite-life intangible — annual impairment test required',
          `Asset ${id}: indefinite useful life. Confirm AASB 136 annual impairment test has been performed (and at the same time each year).`,
          { rowId: id }));
      }
      return;
    }

    // R04 useful-life sanity
    if (life! > 20) {
      findings.push(fb.build('AASB138.R04', 'info', 'Long useful life on intangible — confirm',
        `Asset ${id}: useful life ${life} years. AASB 138.97: useful lives >20 years for finite-life intangibles are unusual; confirm rationale.`,
        { rowId: id, evidence: { life } }));
    }

    // R05 amortisation recalc (straight-line, residual zero by default)
    if (acqD) {
      const monthsElapsed = Math.max(0, Math.min(monthsBetween(acqD, asAt), life! * 12));
      const monthly = cost! / (life! * 12);
      const expectedAcc = Math.min(monthly * monthsElapsed, cost!);
      const tol = Math.max(50, 0.005 * cost!);
      const delta = acc! - expectedAcc;
      if (Math.abs(delta) > tol) {
        const sev: Finding['severity'] = Math.abs(delta) > 2 * tol ? 'error' : 'warning';
        findings.push(fb.build('AASB138.R05', sev, 'Straight-line amortisation differs from reported',
          `Asset ${id}: reported acc $${fmt(acc!)}, expected $${fmt(expectedAcc)} (${monthsElapsed}/${life! * 12} months × $${fmt(monthly)}/month). Delta ${delta >= 0 ? '+' : '−'}$${fmt(Math.abs(delta))}.`,
          { rowId: id, evidence: { reported: acc, expected: expectedAcc }, suggestedAdjustment: { field: 'accumulated_amortisation', from: acc!, to: +expectedAcc.toFixed(2), rationale: 'Straight-line over useful life.' } }));
      }
    }

    // R06 internal consistency carrying = cost - acc
    const expectedCV = cost! - acc!;
    const tol = Math.max(50, 0.005 * cost!);
    if (Math.abs(cv! - expectedCV) > tol) {
      findings.push(fb.build('AASB138.R06', 'warning', 'Carrying value does not reconcile to cost − accumulated amortisation',
        `Asset ${id}: carrying $${fmt(cv!)}, expected $${fmt(expectedCV)}.`,
        { rowId: id, evidence: { cv, expectedCV }, suggestedAdjustment: { field: 'carrying_value', from: cv!, to: +expectedCV.toFixed(2), rationale: 'Cost − accumulated amortisation.' } }));
    }

    // R07 carrying not negative
    if (cv! < 0) {
      findings.push(fb.build('AASB138.R07', 'error', 'Negative carrying value', `Asset ${id}: carrying $${fmt(cv!)}.`, { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_138' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
